import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useGetSuperPickemData } from "../../../hooks/data/useGetSuperPickemData";
import { useDispatch } from "react-redux";
import { userLogoutAction } from "../../../redux/actions/loginImport";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { DateTime } from 'luxon';

const SuperPickemContext = createContext();

export const useSuperPickemContext = () => {
    const context = useContext(SuperPickemContext);
    if (!context) {
        throw new Error("useSuperPickemContext must be used within a SuperPickemProvider");
    }
    return context;
}

export const SuperPickemProvider = ({ children }) => {
    const {
        userData,
        gameData,
        masterPoolData,
        pickState,
        intervalInfo,
        hasJoinDeadlinePassed,
        leaderboardData,
        interval,
        setInterval,
    } = useUserAndGameContext();

    const location = useLocation();
    // Use useMemo to create a stable searchParams object
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const uriEncoded = (link) => {
        return encodeURI(`${encodeURIComponent(link)}`);
    }

    const timeNowInNewYork = DateTime.now().setZone('America/New_York');
    const firstGameTime = masterPoolData?.gameParameters?.firstGameTime;
    const firstGameTimeInNewYork = DateTime.fromISO(firstGameTime, { zone: 'America/New_York' });
    const hasFirstGameStarted = timeNowInNewYork > firstGameTimeInNewYork;
    
    const firstBracketGameTime = masterPoolData?.gameParameters?.firstBracketGameTime;
    const firstBracketGameTimeInNewYork = DateTime.fromISO(firstBracketGameTime, { zone: 'America/New_York' });
    const hasBracketDeadlinePassed = timeNowInNewYork > firstBracketGameTimeInNewYork;

    const redisKeys = masterPoolData?.redisKeys;

    const hasFinalStarted = masterPoolData?.gameParameters?.hasFinalStarted;

    const [hasDataFetched, setHasDataFetched] = useState(false);
    const [intervalOptions, setIntervalOptions] = useState(null);
    const [selectedUserBracketInfo, setSelectedUserBracketInfo] = useState(null);
    //console.log("SelectedUserBracketInfo in SuperPickemProvider: ", selectedUserBracketInfo);
     // Check for user parameter in URL
     useEffect(() => {
        const userParam = searchParams.get('user');
        if (userParam && leaderboardData) {
            const selectedUserEntry = leaderboardData?.find(entry => entry.username === userParam);
            //console.log("selectedUserEntry in BracketGamesProvider: ", selectedUserEntry);
            
            // Completely replace the previous state instead of merging
            setSelectedUserBracketInfo({
                username: userParam,
                bracketPicks: selectedUserEntry?.bracketPicks || [],
                seriesLengths: selectedUserEntry?.seriesLengths || [],
            });
        } else if (!userParam) {
            // Clear selection when no user parameter is present
            setSelectedUserBracketInfo(null);
        }
    }, [searchParams, leaderboardData]);
    
    // Maintain two separate states:
    // 1. todaysInterval - The "default" interval based on today's date (doesn't change with navigation)
    // 2. currentInterval - The currently selected interval (changes with navigation)
    const [todaysInterval, setTodaysInterval] = useState(null);
    const [currentInterval, setCurrentInterval] = useState(null);

    const { mutate: getSuperPickemData, data: superPickemData, isLoading: superPickemDataLoading, isError: superPickemDataError, error: superPickemDataErrorMessage } = useGetSuperPickemData();
    
    // Use Effect to get super pickem data
    useEffect(() => {
        if (redisKeys && !hasDataFetched) {
            getSuperPickemData({ redisKeys });
            setHasDataFetched(true);
        }
    }, [getSuperPickemData, redisKeys, hasDataFetched]);

    // Handle errors
    useEffect(() => {
        if (superPickemDataError) {
            console.error('Error fetching super pickem data:', superPickemDataErrorMessage);
            if (superPickemDataError?.response?.status === 401) {
                dispatch(userLogoutAction({
                    logoutType: 'token-expired'
                }));
                navigate('/login');
            } else {
                navigate('/404/pool-hall');
            }
        }
    }, [superPickemDataError, superPickemDataErrorMessage, dispatch, navigate]);

    const { tournamentSchedule, teams, gameSchedule, gameOdds } = superPickemData || {};

    const usingSeriesLengths = masterPoolData?.gameParameters?.usingSeriesLengths;
    const picksToMake = masterPoolData?.gameParameters?.picksToMake;
    const picksInfo = masterPoolData?.gameParameters?.picksInfo;
    const rounds = masterPoolData?.gameParameters?.rounds;
    const lastRound = rounds ? rounds[rounds.length - 1] : null;
    const intervalType = intervalInfo?.type;

   // Use Effect to set interval options with Luxon
    useEffect(() => {
        if (intervalInfo && intervalType && gameSchedule) {
            if (intervalType === 'daily') {
                const startDate = DateTime.fromISO(intervalInfo?.startDate);
                const endDate = DateTime.fromISO(intervalInfo?.endDate);
                
                // Filter games within date range and not with "NotNecessary" status
                const validGames = gameSchedule?.filter(game => {
                    const gameDate = DateTime.fromISO(game.Day);
                    return gameDate >= startDate && 
                        gameDate <= endDate && 
                        game.Status !== "NotNecessary";
                });
                
                // Extract unique dates from valid games
                const uniqueDates = [...new Set(validGames.map(game => game.Day))];
                
                // Sort dates chronologically
                uniqueDates.sort((a, b) => DateTime.fromISO(a) < DateTime.fromISO(b) ? -1 : 1);
                
                // Set the interval options
                setIntervalOptions(uniqueDates);
            }
        }
    }, [intervalInfo, intervalType, gameSchedule]);

    // Find the appropriate interval for today
    const findTodaysInterval = (intervalOptions, timeNowInNewYork) => {
        // Check if it's before 3AM in New York
        const isBeforeThreeAM = timeNowInNewYork.hour < 3;
        
        // Get today's and yesterday's dates
        const today = timeNowInNewYork.startOf('day');
        const yesterday = today.minus({ days: 1 });
        
        // If before 3AM, look for yesterday's date first
        if (isBeforeThreeAM) {
          // Try to find yesterday's date in options
          const yesterdayOption = intervalOptions.find(option => 
            DateTime.fromISO(option).hasSame(yesterday, 'day')
          );
          
          if (yesterdayOption) return yesterdayOption;
          
          // If no yesterday, look for today or future dates
          const todayOrFutureOption = intervalOptions.find(option => 
            DateTime.fromISO(option) >= today
          );
          
          if (todayOrFutureOption) return todayOrFutureOption;
        } else {
          // After 3AM, look for today or future dates
          const todayOrFutureOption = intervalOptions.find(option => 
            DateTime.fromISO(option) >= today
          );
          
          if (todayOrFutureOption) return todayOrFutureOption;
        }
        
        // If no games left, fall back to the most recent date
        return intervalOptions[intervalOptions.length - 1];
    };

    // Check if a given interval is valid (exists in the options)
    const isValidInterval = (interval) => {
        return intervalOptions?.includes(interval);
    };
  
    // This effect sets both todaysInterval and initializes currentInterval
    useEffect(() => {
        if (intervalOptions?.length > 0 && timeNowInNewYork && intervalType === 'daily') {
            // Calculate today's interval once (won't change with navigation)
            const calculatedTodayInterval = findTodaysInterval(intervalOptions, timeNowInNewYork);
            setTodaysInterval(calculatedTodayInterval);
            
            // Get interval from URL if present
            const urlInterval = searchParams.get('interval');
            
            // Initialize currentInterval with URL param if valid, otherwise use today's interval
            if (urlInterval && isValidInterval(urlInterval)) {
                setCurrentInterval(urlInterval);
                setInterval(urlInterval);
            } else {
                setCurrentInterval(calculatedTodayInterval);
                setInterval(calculatedTodayInterval);
                
                // Update URL with today's interval if no valid interval in URL
                const newSearchParams = new URLSearchParams(location.search);
                newSearchParams.set('interval', calculatedTodayInterval);
                const newUrl = `${location.pathname}?${newSearchParams.toString()}`;
                window.history.replaceState(null, '', newUrl);
            }
        }
    }, [intervalOptions, intervalType, timeNowInNewYork, location.search, searchParams, setInterval, location.pathname]);
    
    const SuperPickemContextValue = useMemo(() => ({
        gameData,
        tournamentSchedule,
        teams,
        usingSeriesLengths,
        picksToMake,
        picksInfo,
        lastRound,
        hasFirstGameStarted,
        hasBracketDeadlinePassed,
        gameSchedule,
        gameOdds,
        intervalOptions,
        intervalType,
        currentInterval,
        todaysInterval,
        selectedUserBracketInfo,
        hasFinalStarted,
    }), [
        gameData, 
        tournamentSchedule, 
        teams, 
        usingSeriesLengths, 
        picksToMake, 
        picksInfo, 
        lastRound, 
        hasFirstGameStarted, 
        hasBracketDeadlinePassed, 
        gameSchedule, 
        gameOdds,
        intervalOptions,
        intervalType,
        currentInterval,
        todaysInterval, 
        selectedUserBracketInfo,
        hasFinalStarted,
    ]);

    return (
        <SuperPickemContext.Provider value={SuperPickemContextValue}>
            {children}
        </SuperPickemContext.Provider>
    );
}

export default SuperPickemContext;